import WebApp from '@twa-dev/sdk';
import { initTelegramChrome } from './telegram-chrome.js';
import { exitTelegramFullscreen } from './telegram-fullscreen.js';

export function initTelegramApp(): void {
  WebApp.ready();
  WebApp.expand();
  // Main Mini App / "Open" launches open in Bot API 8.0 fullscreen mode, where the
  // ✕/⋮ chrome floats over the content. Drop back to the normal header so nothing
  // slides under the chrome. Re-applied defensively in case the client (re)enters
  // fullscreen slightly after launch. See lib/telegram-fullscreen.ts.
  exitTelegramFullscreen();
  WebApp.onEvent('viewportChanged', exitTelegramFullscreen);
  setTimeout(exitTelegramFullscreen, 150);
  setTimeout(exitTelegramFullscreen, 600);
  // Paint the native header/background to --void and mirror Telegram safe-area
  // insets into CSS vars (kills the white header rectangle, keeps both launch
  // entry points consistent).
  initTelegramChrome();
  syncTheme();
  WebApp.onEvent('themeChanged', syncTheme);
}

export function getInitData(): string {
  // Read directly from window.Telegram.WebApp at call time — the @twa-dev/sdk
  // import captures a reference at module load, BEFORE the dev-mock can replace
  // the empty shell installed by telegram-web-app.js (see telegram-dev-mock.ts).
  const tg = (globalThis as { Telegram?: { WebApp?: { initData?: string } } }).Telegram;
  return tg?.WebApp?.initData ?? '';
}

/**
 * Read Telegram first_name from initDataUnsafe at call time.
 * Returns null when the WebApp shell or user payload is absent (e.g. running
 * outside Telegram and the dev-mock has not yet installed its shell).
 * Spec 016-onboarding-fidelity-fix FR-002 / T011.
 */
export function getTelegramFirstName(): string | null {
  const tg = (globalThis as {
    Telegram?: { WebApp?: { initDataUnsafe?: { user?: { first_name?: string } } } };
  }).Telegram;
  const name = tg?.WebApp?.initDataUnsafe?.user?.first_name;
  return name && name.trim().length > 0 ? name : null;
}

type WriteAccessWebApp = {
  initDataUnsafe?: { user?: { allows_write_to_pm?: boolean } };
  isVersionAtLeast?: (version: string) => boolean;
  requestWriteAccess?: (callback: (granted: boolean) => void) => void;
};

function getWriteAccessWebApp(): WriteAccessWebApp | undefined {
  return (globalThis as { Telegram?: { WebApp?: WriteAccessWebApp } }).Telegram?.WebApp;
}

// Telegram does not refresh initDataUnsafe after the user allows messages, so
// remember a granted answer for the rest of the session.
let botWriteGranted = false;

/** Test-only: forget the remembered grant. */
export function resetBotWriteGrantedForTests(): void {
  botWriteGranted = false;
}

/** true: the bot may message this user (they started the bot chat or allowed it); false: not allowed; null: unknown (outside Telegram). */
export function canBotWrite(): boolean | null {
  if (botWriteGranted) return true;
  const user = getWriteAccessWebApp()?.initDataUnsafe?.user;
  if (!user) return null;
  return user.allows_write_to_pm === true;
}

/**
 * Ask Telegram «Allow the bot to message you?». Resolves true when allowed
 * (immediately, without a prompt, when canBotWrite() is already true),
 * false when refused, unsupported (no requestWriteAccess, or isVersionAtLeast('6.9') is false),
 * outside Telegram, or when Telegram does not answer within timeoutMs. Never rejects.
 */
export function requestBotWriteAccess(timeoutMs = 10_000): Promise<boolean> {
  return new Promise<boolean>((resolve) => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    let done = false;
    const finish = (value: boolean): void => {
      if (done) return;
      done = true;
      if (timer !== undefined) clearTimeout(timer);
      if (value) botWriteGranted = true;
      resolve(value);
    };
    try {
      if (canBotWrite() === true) {
        finish(true);
        return;
      }
      const wa = getWriteAccessWebApp();
      if (wa && typeof wa.requestWriteAccess === 'function' && wa.isVersionAtLeast?.('6.9') !== false) {
        timer = setTimeout(() => finish(false), timeoutMs);
        wa.requestWriteAccess((granted) => finish(Boolean(granted)));
        return;
      }
      finish(false);
    } catch {
      finish(false);
    }
  });
}

export function enableClosingConfirmation(): void {
  WebApp.enableClosingConfirmation();
}

export function disableClosingConfirmation(): void {
  WebApp.disableClosingConfirmation();
}

function syncTheme(): void {
  const params = WebApp.themeParams;
  const root = document.documentElement;
  if (params.bg_color) root.style.setProperty('--tg-bg', params.bg_color);
  if (params.text_color) root.style.setProperty('--tg-text', params.text_color);
  if (params.hint_color) root.style.setProperty('--tg-hint', params.hint_color);
  if (params.link_color) root.style.setProperty('--tg-link', params.link_color);
  if (params.button_color) root.style.setProperty('--tg-button', params.button_color);
  if (params.button_text_color) root.style.setProperty('--tg-button-text', params.button_text_color);
  if (params.secondary_bg_color) root.style.setProperty('--tg-secondary-bg', params.secondary_bg_color);
  root.dataset.colorScheme = WebApp.colorScheme;
}
